import { useState } from 'react';
import { css } from '@emotion/react';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { useTranslation } from '@frontend/i18n';
import { Button, DropdownField, useAlert, useFormField } from '@frontend/design-system';
import { SCHEDULING_MODE_OPTIONS } from './constants';
import { SchedulingMode } from './types';

type Props = {
  locationId: string;
  value: SchedulingMode;
};

export const SchedulingModeEditor = ({ locationId, value }: Props) => {
  const { t } = useTranslation('schedule-settings');
  const alert = useAlert();

  const [savedValue, setSavedValue] = useState(value);
  const fieldProps = useFormField({ type: 'dropdown', value });

  const updateLocationSettingsMutation = SchedulerV3.Mutations.useUpdateLocationSettingsMutation({
    options: {
      onSuccess: (_response, request) => {
        setSavedValue(request.osChannelMode as SchedulingMode);
      },
      onError: (error) => {
        console.error('Failed to update scheduling mode', error);
        alert.error(t('Failed to update scheduling mode'));
      },
    },
  });

  return (
    <div css={actionContainerStyles}>
      <DropdownField
        {...fieldProps}
        css={{ width: 300 }}
        label={t('Scheduling Mode')}
        name={`scheduling-mode-${locationId}`}
      >
        {SCHEDULING_MODE_OPTIONS.map((option) => (
          <DropdownField.Option key={option.value} value={option.value}>
            {option.label}
          </DropdownField.Option>
        ))}
      </DropdownField>
      <Button
        size='medium'
        variant={fieldProps.value === savedValue ? 'secondary' : 'primary'}
        css={{ width: 56 }} // Setting a fixed width to prevent button resizing and dropdown shift when showing loader while saving
        disabled={updateLocationSettingsMutation.isLoading || fieldProps.value === savedValue}
        loading={updateLocationSettingsMutation.isLoading}
        onClick={() =>
          updateLocationSettingsMutation.mutate({
            locationId,
            osChannelMode: fieldProps.value,
          })
        }
      >
        {t('Save')}
      </Button>
    </div>
  );
};

const actionContainerStyles = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-md);
`;
